// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Per-poll asset status and telemetry state.
 *
 * Extracted from `kgModel.ts`'s `deriveLiveState` when Asset Topology was
 * replaced by Power Flow. It was coupled to the type-grouped model — it
 * iterated that model's group membership — but nothing about the derivation is:
 * it is "for these entity ids, what is the current status and aggregated
 * state". Both the Power Flow page and `AssetDetailPanelHost` need exactly
 * that, over different id sets.
 *
 * This is the *live* half of the structure/live split (design §7.5). It is
 * rebuilt on every 60-second poll, while the geometry it sits beside is not —
 * which is why a poll re-renders identical coordinates with different colours
 * instead of moving the diagram under the operator's hand.
 */

import type { Entity, EntityState, Relationship } from '../types/digitalTwin';
import {
  aggregateChildStatus,
  aggregateTelemetryState,
  buildTelemetryChildIndex,
  categorizeStatus,
  getStatusToken,
  type StatusToken,
} from './entityHelpers';

export interface AssetLiveState {
  /** Entity id → status token. */
  statusOf: Map<string, StatusToken>;
  /** Entity id → the aggregated state the detail panel reads. */
  stateOf: Map<string, EntityState | undefined>;
}

/**
 * Status precedence when rolling several assets up to one indicator.
 *
 * `idle` ranks *below* `ok`, so a set of standby units with one online unit
 * reads as online rather than as standby. Carried over unchanged from the
 * type-grouped model, where it was tuned against the design prototype.
 */
const STATUS_RANK: Record<StatusToken, number> = { fault: 3, warn: 2, ok: 1, idle: 0 };

export const worstStatus = (tokens: readonly StatusToken[]): StatusToken => {
  let worst: StatusToken = 'idle';
  for (const token of tokens) {
    if (STATUS_RANK[token] > STATUS_RANK[worst]) worst = token;
  }
  return worst;
};

export const EMPTY_LIVE_STATE: AssetLiveState = { statusOf: new Map(), stateOf: new Map() };

/**
 * Derive status and state for `ids`.
 *
 * The telemetry child index is built once here. Without it,
 * `aggregateChildStatus` and `aggregateTelemetryState` each rescan the whole
 * relationship array per entity — quadratic on a site with thousands of
 * telemetry points.
 *
 * @param ids Which entities to derive for. Pass the diagram's assets rather
 *            than the whole payload: a site's telemetry points outnumber its
 *            equipment by an order of magnitude and none of them is rendered.
 */
export const deriveAssetLiveState = (
  entities: readonly Entity[],
  relationships: readonly Relationship[],
  ids: readonly string[]
): AssetLiveState => {
  const entityMap = new Map(entities.map(entity => [entity.id, entity]));
  const entityList = entities as Entity[];
  const relationshipList = relationships as Relationship[];
  const childIndex = buildTelemetryChildIndex(relationshipList);

  const statusOf = new Map<string, StatusToken>();
  const stateOf = new Map<string, EntityState | undefined>();

  for (const id of ids) {
    const entity = entityMap.get(id);
    if (!entity) continue;
    const status = aggregateChildStatus(
      entity,
      entityList,
      relationshipList,
      entityMap,
      childIndex
    );
    statusOf.set(id, getStatusToken(categorizeStatus(status)));
    stateOf.set(
      id,
      aggregateTelemetryState(entity, entityList, relationshipList, entityMap, childIndex)
    );
  }

  return { statusOf, stateOf };
};
